import { ButtonTertiary } from "@nova/component-btn"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { useMemo, useState } from "react"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import {
    ActivitySystemRow,
    ActivityTimelineHeader,
    ActivityTimelineLine,
} from "./ActivityTimelineItem"
import { isSystemEvent } from "./activityIcons"
import { ActorNames, filterGroupedBySearch } from "./activitySearch"
import {
    foldRunLines,
    groupTimeline,
    hiddenRunCount,
    RUN_PREVIEW_COUNT,
    TimelineRun,
    visibleRunEvents,
} from "./groupTimeline"
import { selectActivitySearch } from "./slice"
import { useActivityTimeline } from "./useActivityData"

const Run = ({
    run,
    actorNames,
    expanded,
    onToggle,
}: {
    run: TimelineRun
    actorNames: ActorNames
    expanded: boolean
    onToggle: () => void
}) => {
    const actorName = actorNames.get(run.createdBy)

    if (run.system) {
        const event = run.events[0]
        if (!isSystemEvent(event)) return null
        return <ActivitySystemRow event={event} actorName={actorName} />
    }

    const visible = visibleRunEvents(run, expanded)
    const hidden = hiddenRunCount(run, expanded)
    const truncatable = run.events.length > RUN_PREVIEW_COUNT
    const lines = foldRunLines(visible)

    return (
        <div className="flex flex-col">
            <ActivityTimelineHeader event={run.events[0]} actorName={actorName} />
            <div className="flex flex-col gap-2 pl-2 pb-4">
                {lines.map((line) => (
                    <ActivityTimelineLine key={line.event.id} line={line} actorNames={actorNames} />
                ))}
            </div>
            {truncatable && (
                <button
                    type="button"
                    onClick={onToggle}
                    className="self-start pl-2 pb-4 text-blue-500 hover:text-white"
                >
                    <small>{hidden > 0 ? `Show ${hidden} more` : "Show less"}</small>
                </button>
            )}
        </div>
    )
}

export const ActivityTimeline = () => {
    const search = useAppSelector(selectActivitySearch)
    const { events, hasMore, isLoading, isLoadingMore, loadMore } = useActivityTimeline()
    const members = useAppSelector(selectWorkspaceMembers)

    const [expandedRuns, setExpandedRuns] = useState<ReadonlySet<string>>(new Set())

    const actorNames = useMemo<ActorNames>(
        () => new Map(members.map((member) => [member.userId, member.fullName])),
        [members]
    )

    const days = useMemo(
        () => filterGroupedBySearch(groupTimeline(events), actorNames, search),
        [events, actorNames, search]
    )

    const toggleRun = (id: string) =>
        setExpandedRuns((current) => {
            const next = new Set(current)
            if (!next.delete(id)) next.add(id)
            return next
        })

    if (isLoading && !events.length) {
        return (
            <div className="flex justify-center py-8">
                <NovaSpinner width={16} height={16} />
            </div>
        )
    }

    const loadMoreButton = hasMore && (
        <div className="flex justify-center pb-4">
            <ButtonTertiary
                title={isLoadingMore ? "Loading..." : "Load more"}
                onClick={loadMore}
                disabled={isLoadingMore}
            />
        </div>
    )

    if (!days.length) {
        return (
            <div className="flex flex-col gap-6">
                <div className="py-8 text-center text-gray-300">
                    {search
                        ? "No loaded activity matches this search."
                        : "No activity in this date range."}
                </div>
                {loadMoreButton}
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {days.map((day, index) => (
                <div key={day.heading} className="flex flex-col gap-1">
                    {index > 0 && <div className="h-px bg-white-100 mb-4" />}
                    <small className="mono text-gray-400">{day.heading}</small>
                    {day.runs.map((run) => (
                        <Run
                            key={run.id}
                            run={run}
                            actorNames={actorNames}
                            expanded={expandedRuns.has(run.id)}
                            onToggle={() => toggleRun(run.id)}
                        />
                    ))}
                </div>
            ))}

            {loadMoreButton}
        </div>
    )
}
